<template>
  <!-- Quiet corner icon (bottom-right). Feedback is a low-frequency action —
       a neutral circular button in the conventional help/feedback spot, with
       the label revealed on hover as a tooltip. -->
  <button
    v-if="!isOpen"
    @click="openModal"
    :class="[
      'fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] lg:bottom-5 right-5 z-40 w-11 h-11 rounded-full flex items-center justify-center',
      'border shadow-(--nviya-shadow-md) transition-all hover:shadow-(--nviya-shadow-lg) hover:scale-105',
      'bg-(--nviya-surface) border-(--nviya-border) text-(--nviya-text-muted) hover:text-(--nviya-text-primary)',
      'dark:bg-surface dark:border-primary/30 dark:text-slate-300 dark:hover:text-white'
      ]"
    title="Feedback"
    aria-label="Send feedback"
  >
    <FontAwesome :icon="['fas', 'comment-dots']" class="w-4 h-4" />
  </button>

  <!-- Modal -->
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center px-4"
        @click.self="closeModal"
      >
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeModal"></div>
        <div :class="[
          'relative w-full max-w-md rounded-xl shadow-(--nviya-shadow-lg) overflow-hidden',
          'bg-white dark:bg-surface'
          ]">
          <!-- Header -->
          <div :class="[
            'flex items-center justify-between px-5 py-4 border-b',
            'border-(--nviya-border) dark:border-primary/30'
            ]">
            <div class="flex items-center gap-2">
              <FontAwesome :icon="['fas', 'comment-dots']" :class="['w-4 h-4', 'text-(--nviya-gold)']" />
              <h2 :class="['typo-section-title', 'text-slate-900 dark:text-white']">Send feedback</h2>
            </div>
            <button
              @click="closeModal"
              :class="['p-1.5 rounded-lg transition-colors', 'hover:bg-(--nviya-figure-bg) text-slate-500 dark:hover:bg-primary/30 dark:text-slate-400']"
              aria-label="Close"
            >
              <FontAwesome :icon="['fas', 'times']" class="w-3 h-3" />
            </button>
          </div>

          <!-- Body -->
          <div v-if="!submitted" class="px-5 py-4 space-y-4">
            <!-- Category chips -->
            <div>
              <label :class="['typo-eyebrow block mb-2 text-(--nviya-text-tertiary)']">Category</label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="opt in categories"
                  :key="opt.value"
                  @click="category = opt.value"
                  :class="[
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors',
                    category === opt.value
                    ? 'bg-(--nviya-blue) text-white border-(--nviya-accent)'
                    : 'bg-white text-slate-600 border-(--nviya-border) hover:border-(--nviya-border-strong) dark:bg-primary/20 dark:text-slate-300 dark:border-primary/30 dark:hover:border-primary/50'
                    ]"
                >
                  <FontAwesome :icon="['fas', opt.icon]" class="w-3 h-3" />
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Message -->
            <div>
              <label for="feedback-message" :class="['typo-eyebrow block mb-2 text-(--nviya-text-tertiary)']">Your feedback</label>
              <textarea
                id="feedback-message"
                ref="textareaRef"
                v-model="message"
                rows="5"
                :placeholder="placeholder"
                :class="[
                  'w-full px-3 py-2 rounded-lg border text-sm outline-none resize-none transition-colors',
                  'bg-(--nviya-surface) border-(--nviya-border) text-(--nviya-text-primary) placeholder:text-(--nviya-text-tertiary) focus:border-(--nviya-accent) focus:ring-2 focus:ring-(--nviya-accent)/20',
                  'dark:bg-primary/20 dark:border-primary/30 dark:text-white dark:placeholder-slate-500 dark:focus:border-info dark:focus:ring-info/30'
                  ]"
              ></textarea>
              <div class="flex items-center justify-between mt-1">
                <span :class="['text-[11px]', 'text-slate-400 dark:text-slate-500']">
                  Sending from {{ currentRoute }}
                </span>
                <span :class="['text-[11px] tabular-nums', message.length > MAX_LENGTH ? 'text-red-500' : 'text-slate-400 dark:text-slate-500']">
                  {{ message.length }}/{{ MAX_LENGTH }}
                </span>
              </div>
            </div>

            <!-- Error -->
            <div
              v-if="error"
              :class="['px-3 py-2 rounded-lg text-xs flex items-center gap-2',
              'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30']"
            >
              <FontAwesome :icon="['fas', 'circle-exclamation']" class="w-3 h-3 shrink-0" />
              {{ error }}
            </div>
          </div>

          <!-- Success state -->
          <div v-else class="px-5 py-8 text-center">
            <div :class="['w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3', 'bg-emerald-100 dark:bg-emerald-500/20']">
              <FontAwesome :icon="['fas', 'check']" :class="['w-5 h-5', 'text-emerald-600 dark:text-emerald-400']" />
            </div>
            <p :class="['typo-section-title mb-1', 'text-slate-900 dark:text-white']">Thanks!</p>
            <p :class="['typo-body', 'text-slate-500 dark:text-slate-400']">Your feedback has been received.</p>
          </div>

          <!-- Footer -->
          <div
            v-if="!submitted"
            :class="[
              'flex items-center justify-end gap-2 px-5 py-3 border-t',
              'bg-(--nviya-figure-bg) border-(--nviya-border) dark:bg-primary/10 dark:border-primary/30'
              ]"
          >
            <button
              @click="closeModal"
              :class="['px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
              'text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-primary/30']"
            >
              Cancel
            </button>
            <button
              @click="submit"
              :disabled="!canSubmit || submitting"
              :class="['flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors',
              canSubmit && !submitting
              ? 'bg-(--nviya-blue) text-white hover:bg-(--nviya-blue)/90'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed dark:bg-primary/20 dark:text-slate-500']"
            >
              <FontAwesome v-if="submitting" :icon="['fas', 'spinner']" class="w-3 h-3 animate-spin" />
              <FontAwesome v-else :icon="['fas', 'paper-plane']" class="w-3 h-3" />
              {{ submitting ? 'Sending…' : 'Send' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { siteFeedbackService } from '@/api/services/siteFeedback'

const MAX_LENGTH = 2000
const AUTO_CLOSE_MS = 1800

const route = useRoute()

const categories = [
  { value: 'bug', label: 'Bug', icon: 'bug', placeholder: 'What broke, and how did you trigger it?' },
  { value: 'idea', label: 'Idea', icon: 'lightbulb', placeholder: "What would make this better?" },
  { value: 'praise', label: 'Praise', icon: 'heart', placeholder: "What's working well?" },
  { value: 'other', label: 'Other', icon: 'comment', placeholder: 'Anything else on your mind?' },
]

const isOpen = ref(false)
const category = ref('bug')
const message = ref('')
const submitting = ref(false)
const submitted = ref(false)
const error = ref('')
const textareaRef = ref(null)

const currentRoute = computed(() => route.fullPath)
const placeholder = computed(() => categories.find((c) => c.value === category.value)?.placeholder || '')
const canSubmit = computed(() => message.value.trim().length > 0 && message.value.length <= MAX_LENGTH)

const openModal = () => {
  isOpen.value = true
  error.value = ''
  nextTick(() => textareaRef.value?.focus())
}

const closeModal = () => {
  isOpen.value = false
  // Reset form after the close transition
  setTimeout(() => {
    if (!isOpen.value) {
      category.value = 'bug'
      message.value = ''
      submitted.value = false
      error.value = ''
    }
  }, 200)
}

const submit = async () => {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true
  error.value = ''
  try {
    await siteFeedbackService.submit({
      category: category.value,
      message: message.value.trim(),
      route: route.fullPath,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      viewport: typeof window !== 'undefined'
        ? { width: window.innerWidth, height: window.innerHeight }
        : { width: null, height: null },
    })
    submitted.value = true
    setTimeout(closeModal, AUTO_CLOSE_MS)
  } catch (e) {
    error.value = e?.response?.data?.detail || e?.message || 'Failed to send feedback. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
